import { useLayoutEffect, useRef } from 'react';
import { Button, Flex, Icon, Text } from '@nula/core-ui';
import { SUMMARY_TEMPLATE_ID } from '@/lib/templates/catalog';
import { useTemplate } from '@/lib/templates/use-templates-store';
import styles from './surface-summarize-row.module.css';

interface SurfaceSummarizeRowProps {
  busy: boolean;
  refusal: string | null;
  onSummarize: () => void;
  onHiddenWhileFocused?: () => void;
}

function joinHeadings(headings: readonly string[]): string {
  if (headings.length <= 1) return headings.join('');
  return `${headings.slice(0, -1).join(', ')} and ${headings[headings.length - 1]}`;
}

export function SurfaceSummarizeRow({
  busy, refusal, onSummarize, onHiddenWhileFocused,
}: SurfaceSummarizeRowProps) {
  const headings = useTemplate(SUMMARY_TEMPLATE_ID)?.sections.map((s) => s.heading) ?? [];
  const rowRef = useRef<HTMLElement>(null);

  // A layout cleanup runs while the row is still in the document, so it can
  // still see whether it held focus before removal drops focus to <body>.
  useLayoutEffect(() => {
    const row = rowRef.current;
    return () => {
      if (row?.contains(document.activeElement)) onHiddenWhileFocused?.();
    };
  }, [onHiddenWhileFocused]);

  return (
    <Flex vertical gap="xs" ref={rowRef} className={styles.wrap}>
      <Flex align="center" className={styles.row}>
        <Flex align="center" justify="center" className={styles.tile} aria-hidden="true">
          <Icon name="summarize" size={18} />
        </Flex>
        <Flex vertical className={styles.text}>
          <Text className={styles.title}>Summarize this recording</Text>
          <Text type="secondary" className={styles.sub}>
            {`${joinHeadings(headings)}, as a new page.`}
          </Text>
        </Flex>
        <Button
          variant="primary"
          size="sm"
          loading={busy}
          disabled={refusal !== null}
          onClick={onSummarize}
        >
          Summarize
        </Button>
      </Flex>
      {refusal && <Text role="alert" className={styles.refusal}>{refusal}</Text>}
    </Flex>
  );
}
